import { useEffect, useState } from 'react';
import AriaModal from 'react-aria-modal';
import { Transition } from 'react-transition-group';
import { ExitHandler, TransitionStatus } from 'react-transition-group/Transition';
import styled from 'styled-components';
import useTheme from '../../utils/useTheme';
import { Icon } from '../Icon/Icon';
import GradientBar, { GradientVariation } from '../GradientBar/GradientBar';

interface IOverlayElementProps {
  state: TransitionStatus;
}

const OverlayElement = styled.div<IOverlayElementProps>`
  background-color: ${props => props.theme.vars.newColor.white};
  width: 100vw;
  height: 100vh;
  z-index: 100;
  padding-top: 0.5rem;
  overflow: hidden;
  transition: transform 0.2s ease-in-out;
  transform: ${({ state }) => (state === 'entered' ? 'translateY(0)' : 'translateY(-100vh)')};
`;

const CloseButtonPanel = styled.button`
  appearance: none;
  box-shadow: none;
  border: none;
  background: transparent;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding-left: 20px;
  align-items: center;
  height: 60px;
  border-bottom: ${props => `1px solid${props.theme.vars.newColor.lightGS1}`};
  transition: background 0.2s ease-in;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.vars.newColor.lightGS1};
  }
`;

const CloseButtonText = styled.span`
  color: ${props => props.theme.vars.newColor.darkGS2};
  font-size: 18px;
  line-height: 1;
  padding-left: 10px;
`;

const ContentElement = styled.div`
  overflow: auto;
`;

export interface IOverlayProps {
  /** Text to appear in the close button */
  closeText: string;
  /** Accessible title text to explain to screen readers what the overlay is for */
  overlayTitle: string;
  /** Is the Overlay visible */
  visible: boolean;
  /** Called when pressing ESC or clicking the close button */
  onCloseButtonClicked: () => void;
  /** Variation of the gradient bar */
  gradientVariation?: GradientVariation;
}

export const Overlay: React.FC<IOverlayProps> = ({
  children,
  closeText,
  gradientVariation,
  onCloseButtonClicked,
  overlayTitle,
  visible,
}) => {
  const theme = useTheme();

  const [modalActive, setModalActive] = useState(visible);
  const [overlayActive, setOverlayActive] = useState(visible);

  useEffect(() => {
    if (visible) {
      openModal();
    } else {
      closeOverlay();
    }
  }, [visible]);

  useEffect(() => {
    let timer: number;
    if (!overlayActive) {
      timer = window.setTimeout(closeModal, 250);
    }
    return () => clearTimeout(timer);
  }, [overlayActive]);

  useEffect(() => {
    if (modalActive) {
      openOverlay();
    }
  }, [modalActive]);

  const openModal = () => {
    setModalActive(true);
  };

  const closeModal = () => {
    setModalActive(false);
  };

  const openOverlay = () => {
    setOverlayActive(true);
  };

  const closeOverlay = () => {
    setOverlayActive(false);
  };

  const getApplicationNode = () => document.getElementById('app-root');

  const handleOnClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();

    closeOverlay();
  };

  const handleOnExited: ExitHandler<HTMLElement> = () => {
    onCloseButtonClicked();
  };

  return (
    <AriaModal
      escapeExits={true}
      focusDialog={false}
      getApplicationNode={getApplicationNode}
      onExit={closeOverlay}
      titleText={overlayTitle}
      underlayClickExits={true}
      underlayColor={null}
      mounted={modalActive}
    >
      <Transition in={overlayActive} timeout={200} onExited={handleOnExited}>
        {state => (
          <OverlayElement data-testid="Overlay" state={state}>
            <GradientBar variation={gradientVariation} />

            <CloseButtonPanel onClick={handleOnClick} data-testid="CloseButtonPanel">
              <Icon icon="chevron-left" size="large" color={theme.vars.newColor.darkGS2} />
              <CloseButtonText>{closeText ? closeText : 'Back'}</CloseButtonText>
            </CloseButtonPanel>
            <ContentElement>{children}</ContentElement>
          </OverlayElement>
        )}
      </Transition>
    </AriaModal>
  );
};
